import axios, {AxiosResponse} from 'axios'
import {ID, Response} from '../../../../_metronic/helpers'
import {
  ClassifiedPost,
  ClassifiedPostQueryResponse,
  ClassifiedCategory,
  ClassifiedCategoryQueryResponse,
  ClassifiedSubCategory,
  ClassifiedSubCategoryQueryResponse,
  ClassifiedStatistics,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const CLASSIFIED_POSTS_URL = `${API_URL}/classified_posts`
const CLASSIFIED_CATEGORIES_URL = `${API_URL}/classified_categories`
const CLASSIFIED_SUBCATEGORIES_URL = `${API_URL}/classified_subcategories`

// ============= POSTS API =============

export const getClassifiedPosts = (query: string): Promise<ClassifiedPostQueryResponse> => {
  return axios.get(`${CLASSIFIED_POSTS_URL}/admin?${query}`).then(({data}: AxiosResponse) => {
    // Transform backend response to match frontend expected structure
    const pagination = data.pagination
    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> =
      []

    // Generate pagination links
    if (pagination) {
      const currentPage = pagination.page || 1
      const totalPages = pagination.last_page || pagination.totalPages || 1

      if (currentPage > 1) {
        links.push({
          label: '&laquo; Previous',
          active: false,
          url: pagination.prev_page_url || null,
          page: currentPage - 1,
        })
      }

      for (let i = 1; i <= totalPages; i++) {
        links.push({
          label: String(i),
          active: i === currentPage,
          url: null,
          page: i,
        })
      }

      if (currentPage < totalPages) {
        links.push({
          label: 'Next &raquo;',
          active: false,
          url: pagination.next_page_url || null,
          page: currentPage + 1,
        })
      }
    }

    return {
      data: data.data,
      ok: data.ok,
      payload: {
        pagination: {
          page: pagination?.page || 1,
          items_per_page: pagination?.items_per_page || pagination?.limit || 10,
          links: links,
        },
      },
    }
  })
}

export const getClassifiedPostById = (id: ID): Promise<ClassifiedPost | undefined> => {
  return axios.get(`${CLASSIFIED_POSTS_URL}/${id}`).then(({data}: AxiosResponse) => {
    return data.data
  })
}

export const createClassifiedPost = (post: ClassifiedPost): Promise<ClassifiedPost | undefined> => {
  return axios
    .post(`${CLASSIFIED_POSTS_URL}/admin/create`, post)
    .then((response: AxiosResponse<Response<ClassifiedPost>>) => response.data)
    .then((response: Response<ClassifiedPost>) => response.data)
}

export const updateClassifiedPost = (post: ClassifiedPost): Promise<ClassifiedPost | undefined> => {
  return axios
    .put(`${CLASSIFIED_POSTS_URL}/${post.id}`, post)
    .then((response: AxiosResponse<Response<ClassifiedPost>>) => response.data)
    .then((response: Response<ClassifiedPost>) => response.data)
}

export const deleteClassifiedPost = (postId: ID): Promise<void> => {
  return axios.delete(`${CLASSIFIED_POSTS_URL}/${postId}`).then(() => {})
}

export const deleteSelectedClassifiedPosts = (postIds: Array<ID>): Promise<void> => {
  const requests = postIds.map((id) => axios.delete(`${CLASSIFIED_POSTS_URL}/${id}`))
  return axios.all(requests).then(() => {})
}

export const restoreClassifiedPost = (postId: ID): Promise<void> => {
  return axios.get(`${CLASSIFIED_POSTS_URL}/restore/${postId}`).then(() => {})
}

export const restoreSelectedClassifiedPosts = (postIds: Array<ID>): Promise<void> => {
  const requests = postIds.map((id) => axios.get(`${CLASSIFIED_POSTS_URL}/restore/${id}`))
  return axios.all(requests).then(() => {})
}

export const approveClassifiedPost = (postId: ID): Promise<ClassifiedPost | undefined> => {
  return axios
    .put(`${CLASSIFIED_POSTS_URL}/admin/${postId}/approve`)
    .then((response: AxiosResponse<Response<ClassifiedPost>>) => response.data)
    .then((response: Response<ClassifiedPost>) => response.data)
}

export const rejectClassifiedPost = (postId: ID): Promise<ClassifiedPost | undefined> => {
  return axios
    .put(`${CLASSIFIED_POSTS_URL}/admin/${postId}/reject`)
    .then((response: AxiosResponse<Response<ClassifiedPost>>) => response.data)
    .then((response: Response<ClassifiedPost>) => response.data)
}

export const featureClassifiedPost = (postId: ID): Promise<ClassifiedPost | undefined> => {
  return axios
    .put(`${CLASSIFIED_POSTS_URL}/admin/${postId}/feature`)
    .then((response: AxiosResponse<Response<ClassifiedPost>>) => response.data)
    .then((response: Response<ClassifiedPost>) => response.data)
}

export const promoteClassifiedPost = (postId: ID): Promise<ClassifiedPost | undefined> => {
  return axios
    .put(`${CLASSIFIED_POSTS_URL}/admin/${postId}/promote`)
    .then((response: AxiosResponse<Response<ClassifiedPost>>) => response.data)
    .then((response: Response<ClassifiedPost>) => response.data)
}

// Upload bulk classified posts
export const importClassifiedPosts = (bulkData): Promise<any> => {
  return axios
    .post(`${CLASSIFIED_POSTS_URL}/admin`, bulkData)
    .then((response: AxiosResponse<Response<any>>) => response.data)
    .then((response: Response<any>) => response.data)
}

export const getClassifiedStatistics = (): Promise<ClassifiedStatistics | undefined> => {
  return axios.get(`${CLASSIFIED_POSTS_URL}/admin/statistics`).then(({data}: AxiosResponse) => {
    return data.data
  })
}

// ============= CATEGORIES API =============

export const getClassifiedCategories = (
  query: string
): Promise<ClassifiedCategoryQueryResponse> => {
  return axios.get(`${CLASSIFIED_CATEGORIES_URL}/admin?${query}`).then(({data}: AxiosResponse) => {
    return data
  })
}

export const getAllClassifiedCategories = (): Promise<ClassifiedCategory[]> => {
  return axios.get(`${CLASSIFIED_CATEGORIES_URL}`).then(({data}: AxiosResponse) => {
    return data.data
  })
}

export const getClassifiedCategoryById = (id: ID): Promise<ClassifiedCategory | undefined> => {
  return axios.get(`${CLASSIFIED_CATEGORIES_URL}/${id}`).then(({data}: AxiosResponse) => {
    return data.data
  })
}

export const createClassifiedCategory = (
  category: ClassifiedCategory
): Promise<ClassifiedCategory | undefined> => {
  return axios
    .post(`${CLASSIFIED_CATEGORIES_URL}/admin`, category)
    .then((response: AxiosResponse<Response<ClassifiedCategory>>) => response.data)
    .then((response: Response<ClassifiedCategory>) => response.data)
}

export const updateClassifiedCategory = (
  category: ClassifiedCategory
): Promise<ClassifiedCategory | undefined> => {
  return axios
    .put(`${CLASSIFIED_CATEGORIES_URL}/admin/${category.id}`, category)
    .then((response: AxiosResponse<Response<ClassifiedCategory>>) => response.data)
    .then((response: Response<ClassifiedCategory>) => response.data)
}

export const deleteClassifiedCategory = (
  categoryId: ID,
  cascade: boolean = false
): Promise<void> => {
  const url = cascade
    ? `${CLASSIFIED_CATEGORIES_URL}/admin/${categoryId}?cascade=true`
    : `${CLASSIFIED_CATEGORIES_URL}/admin/${categoryId}`
  return axios.delete(url).then(() => {})
}

export const deleteSelectedClassifiedCategories = (
  categoryIds: Array<ID>,
  cascade: boolean = false
): Promise<void> => {
  const requests = categoryIds.map((id) => deleteClassifiedCategory(id, cascade))
  return Promise.all(requests).then(() => {})
}

export const restoreClassifiedCategory = (categoryId: ID): Promise<void> => {
  return axios.get(`${CLASSIFIED_CATEGORIES_URL}/restore/${categoryId}`).then(() => {})
}

export const restoreSelectedClassifiedCategories = (categoryIds: Array<ID>): Promise<void> => {
  const requests = categoryIds.map((id) => axios.get(`${CLASSIFIED_CATEGORIES_URL}/restore/${id}`))
  return axios.all(requests).then(() => {})
}

// ============= SUBCATEGORIES API =============

export const getClassifiedSubCategories = (
  query: string
): Promise<ClassifiedSubCategoryQueryResponse> => {
  return axios
    .get(`${CLASSIFIED_SUBCATEGORIES_URL}/admin?${query}`)
    .then(({data}: AxiosResponse) => {
      return data
    })
}

export const getClassifiedSubCategoriesByCategory = (
  categoryId: ID
): Promise<ClassifiedSubCategory[]> => {
  return axios
    .get(`${CLASSIFIED_SUBCATEGORIES_URL}/category/${categoryId}`)
    .then(({data}: AxiosResponse) => {
      return data.data
    })
}

export const getClassifiedSubCategoryById = (
  id: ID
): Promise<ClassifiedSubCategory | undefined> => {
  return axios.get(`${CLASSIFIED_SUBCATEGORIES_URL}/${id}`).then(({data}: AxiosResponse) => {
    return data.data
  })
}

export const createClassifiedSubCategory = (
  subCategory: ClassifiedSubCategory
): Promise<ClassifiedSubCategory | undefined> => {
  return axios
    .post(`${CLASSIFIED_SUBCATEGORIES_URL}/admin`, subCategory)
    .then((response: AxiosResponse<Response<ClassifiedSubCategory>>) => response.data)
    .then((response: Response<ClassifiedSubCategory>) => response.data)
}

export const updateClassifiedSubCategory = (
  subCategory: ClassifiedSubCategory
): Promise<ClassifiedSubCategory | undefined> => {
  return axios
    .put(`${CLASSIFIED_SUBCATEGORIES_URL}/admin/${subCategory.id}`, subCategory)
    .then((response: AxiosResponse<Response<ClassifiedSubCategory>>) => response.data)
    .then((response: Response<ClassifiedSubCategory>) => response.data)
}

export const deleteClassifiedSubCategory = (
  subCategoryId: ID,
  cascade: boolean = false
): Promise<void> => {
  const url = cascade
    ? `${CLASSIFIED_SUBCATEGORIES_URL}/admin/${subCategoryId}?cascade=true`
    : `${CLASSIFIED_SUBCATEGORIES_URL}/admin/${subCategoryId}`
  return axios.delete(url).then(() => {})
}

export const deleteSelectedClassifiedSubCategories = (
  subCategoryIds: Array<ID>,
  cascade: boolean = false
): Promise<void> => {
  const requests = subCategoryIds.map((id) => deleteClassifiedSubCategory(id, cascade))
  return Promise.all(requests).then(() => {})
}

export const restoreClassifiedSubCategory = (subCategoryId: ID): Promise<void> => {
  return axios.get(`${CLASSIFIED_SUBCATEGORIES_URL}/restore/${subCategoryId}`).then(() => {})
}

export const restoreSelectedClassifiedSubCategories = (
  subCategoryIds: Array<ID>
): Promise<void> => {
  const requests = subCategoryIds.map((id) =>
    axios.get(`${CLASSIFIED_SUBCATEGORIES_URL}/restore/${id}`)
  )
  return axios.all(requests).then(() => {})
}
